import { Fragment } from "react"
import { observer } from "mobx-react"
import classNames from "classnames"
import { Menu, Transition } from "@headlessui/react"
import { useStore } from "@features/common"
import { IconChevronDown } from "@lib/icons"
import { Languages, LanguageISO } from "@providers"

type SwitchLanguageProps = {
  disabled?: boolean
  // TODO: костыль для китайского FAQ
  chinese?: boolean
  theme?: "light" | "dark"
  direction?: "bottom" | "top"
  accessLanguages?: string[]
}

export const LanguageSwitcher = observer(function LanguageSwitcher(
  props: SwitchLanguageProps,
) {
  const { disabled, theme = "light", direction = "bottom", chinese } = props
  const { GlobalModel } = useStore()

  const initialLanguages = props.accessLanguages || GlobalModel.accessLanguages

  if (initialLanguages.length === 1 || GlobalModel.isPureDating) {
    return null
  }

  const accessLanguages = chinese
    ? [...initialLanguages, "zh"]
    : initialLanguages

  const options = accessLanguages
    .filter((lang) => lang !== GlobalModel.lang)
    .map((lang) => ({
      value: lang,
      label: Languages[lang].shortname,
    }))

  function clickHandler(lang: LanguageISO) {
    return function setLanguage() {
      // TODO: add await and disable
      GlobalModel.changeLang(lang)
    }
  }

  return (
    <Menu
      as="div"
      className={classNames("relative min-w-[75px] inline-flex text-left", {
        "pointer-events-none": disabled,
      })}
    >
      {({ open }) => (
        <Fragment>
          <Menu.Button
            disabled={disabled}
            className={classNames(
              "space-x-1 pt-3.5 pb-2.5 px-2 w-full h-10 inline-flex items-center justify-between font-sans text-lg tracking-widest1024 uppercase border border-solid transition-colors duration-100 ease-in",
              "mobile-m:px-3 mobile-m:space-x-1.5",
              {
                "text-white": theme === "dark" && !open,
                "text-black bg-white": theme !== "dark" || open,
                "border-black": open,
                "border-transparent": !open,
              },
            )}
            data-test-id="language_switcher__opener"
          >
            <span>{GlobalModel.langShortname}</span>
            <IconChevronDown
              className={classNames(
                "transition-transform duration-200 ease-in transform -translate-y-0.5",
                `${theme === "dark" && !open ? "text-white" : "text-black"}`,
                {
                  "rotate-180": open,
                },
              )}
            />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Menu.Items
              className={classNames(
                "absolute z-30 left-0 right-0 -mt-px flex flex-col origin-top-right bg-white border border-black divide-y divide-black focus:outline-none",
                `${direction === "top" ? "bottom-full" : "top-full"}`,
              )}
            >
              {options.map((option) => (
                <Menu.Item
                  key={`lang-option-${option.value}`}
                  as="div"
                  className={classNames(
                    "pt-3.5 pb-2.5 px-3 text-black",
                    theme === "dark" && "hover:bg-black hover:text-white",
                  )}
                >
                  <button
                    className="w-full flex items-center font-sans text-lg tracking-wider uppercase text-inherit"
                    onClick={clickHandler(option.value as LanguageISO)}
                    data-test-id={`language_switcher__option_${option.value}`}
                  >
                    {option.label}
                  </button>
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Fragment>
      )}
    </Menu>
  )
})

export interface OptionModel<T = string> {
  value: T
  label: string
}
